import {
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { Link } from 'react-router-dom';

import {
  boatConnectionDetail,
  boatCoreVersionLabel,
  boatEnforcementNotice,
} from '../../lib/boatDetails';
import { boatPresenceLabel, type BoatPresence } from '../../lib/boatPresence';
import { formatRelative } from '../../lib/deviceSeen';
import type { Boat } from '../../lib/useBoats';
import { ChevronRightIcon, CloseIcon } from './icons';

export interface BoatDetailsAnchor {
  left: number;
  top: number;
}

export interface BoatDetailsDialogProps {
  boat: Boat;
  presence: BoatPresence;
  now: number;
  canSwitchBoats: boolean;
  open: boolean;
  anchor: BoatDetailsAnchor;
  onDismiss: (restoreFocus?: boolean) => void;
}

function exactContactTime(value: string | null): string | undefined {
  if (value === null) return undefined;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return undefined;
  return parsed.toLocaleString();
}

/**
 * One responsive information surface: an anchored panel on desktop and a
 * bottom sheet on phones. Native dialog supplies top-layer rendering,
 * background inertness, and focus containment without a second menu model.
 */
export function BoatDetailsDialog({
  boat,
  presence,
  now,
  canSwitchBoats,
  open,
  anchor,
  onDismiss,
}: BoatDetailsDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openedMobileRef = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      openedMobileRef.current = window.matchMedia('(max-width: 768px)').matches;
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => closeRef.current?.focus());
    // Mobile browser chrome changes the viewport height while scrolling. Keep
    // the bottom sheet open for those same-breakpoint resizes. A desktop panel
    // needs a fresh anchor; crossing either way also invalidates the trigger.
    const onResize = () => {
      const mobileNow = window.matchMedia('(max-width: 768px)').matches;
      if (!mobileNow || mobileNow !== openedMobileRef.current) onDismiss(true);
    };
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('resize', onResize);
    };
  }, [onDismiss, open]);

  const connection = boatPresenceLabel(presence);
  const enforcement = boatEnforcementNotice(boat.enforcementState);
  const exactContact = exactContactTime(boat.last_contacted_at);
  const style = {
    '--boat-details-left': `${anchor.left}px`,
    '--boat-details-top': `${anchor.top}px`,
  } as CSSProperties;

  function dismissFromBackdrop(event: PointerEvent<HTMLDialogElement>) {
    if (event.target === event.currentTarget) onDismiss(true);
  }

  function keepFocusInside(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== 'Tab') return;
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((control) => control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !event.currentTarget.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !event.currentTarget.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      id="boat-details-dialog"
      className="boat-details-dialog"
      style={style}
      aria-labelledby="boat-details-title"
      data-testid="boat-details-dialog"
      onPointerDown={dismissFromBackdrop}
      onKeyDown={keepFocusInside}
      onCancel={(event) => {
        event.preventDefault();
        onDismiss(true);
      }}
      onClose={() => {
        if (open) onDismiss(true);
      }}
    >
      <section className="boat-details-panel">
        <header className="boat-details-head">
          <div className="boat-details-heading">
            <span className="boat-details-eyebrow">Boat details</span>
            <h2 id="boat-details-title" title={boat.display_name}>
              {boat.display_name}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="boat-details-close"
            aria-label="Close boat details"
            onClick={() => onDismiss(true)}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="boat-details-connection">
          <span className={`boat-status-dot boat-status-dot--${presence}`} aria-hidden="true" />
          <div>
            <span className="boat-details-label">Connection</span>
            <strong data-testid="boat-details-connection">{connection}</strong>
            <span className="boat-details-support">{boatConnectionDetail(presence)}</span>
          </div>
        </div>

        <dl className="boat-details-list">
          <div className="boat-details-row">
            <dt>Last contacted</dt>
            <dd>
              {boat.last_contacted_at === null ? (
                'Never contacted'
              ) : (
                <time dateTime={boat.last_contacted_at} title={exactContact}>
                  {formatRelative(boat.last_contacted_at, now)}
                </time>
              )}
              {exactContact ? <span className="boat-details-support">{exactContact}</span> : null}
            </dd>
          </div>
          <div className="boat-details-row">
            <dt>Core version</dt>
            <dd>
              <strong data-testid="boat-details-core-version">
                {boatCoreVersionLabel(boat.core_version)}
              </strong>
              <span className="boat-details-support">
                {boat.core_version === null
                  ? "This boat hasn't reported a version yet"
                  : 'Last reported by this boat'}
              </span>
            </dd>
          </div>
        </dl>

        {enforcement ? (
          <div
            className={`boat-details-notice boat-details-notice--${enforcement.tone}`}
            data-testid="boat-details-enforcement"
          >
            <strong>{enforcement.title}</strong>
            <span>{enforcement.detail}</span>
          </div>
        ) : null}

        <footer className="boat-details-actions" data-action-count={canSwitchBoats ? '2' : '1'}>
          <Link
            to={`/dashboard/boats/${boat.id}/settings`}
            className="boat-details-action"
            data-testid="boat-details-settings"
            onClick={() => onDismiss(false)}
          >
            Boat settings
            <ChevronRightIcon />
          </Link>
          {canSwitchBoats ? (
            <Link
              to="/dashboard"
              className="boat-details-action"
              data-testid="boat-details-switch"
              onClick={() => onDismiss(false)}
            >
              Switch boat
              <ChevronRightIcon />
            </Link>
          ) : null}
        </footer>
      </section>
    </dialog>
  );
}
